// Pure helpers for the Social Media Post Creator.
//
// Kept in their own module (no React/UI imports) so the create-post contract
// can be unit-tested in isolation. See GitHub issue #121: the Post Preview
// advertised "Select a product or upload an image", but the form had no upload
// control and never sent an `image_url`, so a custom picture could not be
// attached. The form now lets the user upload an image; this builder makes sure
// that uploaded image (falling back to the selected product's image) is carried
// into the create-post request.

import type { CreateSocialPostInput, SocialPlatform } from '@/features/communications';

export interface BuildSocialPostInput {
  platform: SocialPlatform;
  caption: string;
  productId?: string;
  // Data URL of a custom image the user uploaded for this post.
  uploadedImageUrl?: string | null;
  // image_url of the selected product, used when no custom image was uploaded.
  productImageUrl?: string | null;
  isScheduled?: boolean;
  scheduledAt?: Date;
}

// Build the create-social-post request body. A user-uploaded image takes
// precedence over the selected product's image; either is sent as `image_url`
// so the post can carry a custom picture independent of a product.
export function buildSocialPostPayload(input: BuildSocialPostInput): CreateSocialPostInput {
  const imageUrl = input.uploadedImageUrl || input.productImageUrl || undefined;
  return {
    platform: input.platform,
    caption: input.caption.trim(),
    ...(input.productId ? { product_ids: [input.productId] } : {}),
    ...(imageUrl ? { image_url: imageUrl } : {}),
    ...(input.isScheduled && input.scheduledAt
      ? { schedule_at: input.scheduledAt.toISOString() }
      : {}),
  };
}

// Read an image File into a data URL so it can be previewed and submitted as
// `image_url` without a separate upload round-trip.
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
